import { Helper } from "../../helpers/helper";
import { DataOption, DropdownDataOption, InputDropdownReplacementProperties } from "../../models/html.models";
import { TelerikUtility } from "../telerik.utility";

export class DropdownsUtility {
    public static updateDropdownOptions<T = any>(selector: any, parentOptionValue: T, optionSelectionCriteria: (value: T, option: DataOption) => boolean) {
        let jSelect = jQuery(selector);

        // get all the available options from the data attribute, if not available, populate it from the existing list
        let dataOptions: DataOption[] = jSelect.data("options");
        if (dataOptions == null || dataOptions.length == 0) {
            dataOptions = this.getDropdownOptions(selector);
        }
        // ensure you do not modify the original object, but keep the unserializable properties
        dataOptions = JSON.parse(JSON.stringify(dataOptions))

        // clear all the current options in the select and populate with only the valid options for the selectedOption
        // store the currently selected value so that it can be set again once the select has been re-populated
        let currentlySelectedValue = jSelect.val();
        jSelect.empty();
        jQuery.each(dataOptions, function (i) {
            let option = dataOptions[i];
            option.isSelected = option.value == currentlySelectedValue;

            if (optionSelectionCriteria(parentOptionValue, option)) {
                jSelect.append(jQuery("<option>").text(option.text).val(option.value));

                if (option.isSelected) {
                    jSelect.val(currentlySelectedValue);
                }
            }
        });

        // check whether any options are available, it may not have initially populated for this type
        if (dataOptions.length > 0) {
            jSelect.trigger("change");
        }
    }

    public static getDropdownOptions(selector: any, saveData: boolean = true): DataOption[] {
        let jSelect = jQuery(selector);
        let options: DataOption[] = [];
        jSelect.find('option').each(function () {
            let jOption = jQuery(this);
            options.push({ value: jOption.val()!, text: jOption.text() });
        });

        // update data options attribute
        if (saveData) {
            jSelect.data("options", options);
        }

        return options;
    }

    /**
     * Ensures it gets all options as strings and only selects the one option if its the only one
     * @param dropdownSelector 
     */
    public static selectOnlyOption(dropdownSelector: any) {
        let jDropdown = jQuery(dropdownSelector);

        let allValidOptions: string[] = [];
        let jOptions = jDropdown.find("option");
        jOptions.each(function () {
            let jOption = jQuery(this);

            let value = jOption.attr("value") || "";
            if (value.length > 0) {
                allValidOptions.push(value);
            }
        });

        if (allValidOptions.length == 1) {
            jDropdown.val(allValidOptions[0]);
            jDropdown.trigger("change");
        }
    }

    /**
     * Handles the replacement of a hidden input with a dropdown, including for date picker fields
     * @param properties 
     */
    public static replaceInputWithDropdown(properties: InputDropdownReplacementProperties) {
        const OTHER_OPTION_VALUE = "_OTHER";
        let propertiesWithDefaults: InputDropdownReplacementProperties = Object.assign(new InputDropdownReplacementProperties(), properties);
        let { inputSelector, dropdownId, isMultiSelect, data: dropdownOptionsData, enableOtherOption } = propertiesWithDefaults;

        // add dropdown and hide input/telerik control
        this.resetInputFromDropdown(inputSelector);
        let jInput = jQuery(inputSelector);
        let jDropdown = jQuery("<select id='" + dropdownId + "'></select>");
        jDropdown.prop("multiple", isMultiSelect);
        jInput.data("dropdown", dropdownId);

        // data population
        let isValidCustomValue = (value: string, data: DropdownDataOption[]) => !Helper.isStringNullOrEmpty(value) && this.findInputValueInList(value, data) == null;
        if (!isMultiSelect) {
            let emptyOption: DropdownDataOption = { value: "", description: "" };
            dropdownOptionsData = [emptyOption].concat(dropdownOptionsData);

            // add the non-empty custom value
            let value = jInput.val().toString();
            if (isValidCustomValue(value, dropdownOptionsData)) {
                if (!enableOtherOption) {
                    dropdownOptionsData.push({ value: value, description: value });
                }
            }
        } else {
            // add non-empty custom values
            let valueList = this.getInputValueAsList(jInput) || [];
            valueList.filter(value => isValidCustomValue(value, dropdownOptionsData)).forEach(item => {
                // multi select option and the other option are not supported together
                dropdownOptionsData.push({ value: item, description: item });
            });
        }

        // add the other option if enabled and not a multi select dropdown
        if (enableOtherOption && !isMultiSelect) {
            dropdownOptionsData.push({ value: OTHER_OPTION_VALUE, description: "Other" });
        }
        dropdownOptionsData.forEach(function (item) {
            jDropdown.append("<option value='" + item.value + "'>" + item.description + "</option>");
        });

        // set current hidden input value to dropdown
        if (!isMultiSelect) {
            let value = jInput.val().toString();
            let isCustomValue = isValidCustomValue(value, dropdownOptionsData);

            if (!enableOtherOption || (enableOtherOption && !isCustomValue)) {
                jDropdown.val(value);
            } else {
                // set to other option
                jDropdown.val(OTHER_OPTION_VALUE);
            }
        } else {
            let value = this.getInputValueAsList(jInput) || [];
            jDropdown.val(value);
        }

        // update hidden input with value if any
        // if the selected value is the other option, clear the input value for manual input if not already custom
        let updateInput = () => {
            let selectedValues = jDropdown.val();
            let currentInputValue = jInput.val().toString();
            if (!isMultiSelect) {
                let isOtherOptionSelected = selectedValues == OTHER_OPTION_VALUE;
                if (enableOtherOption && isOtherOptionSelected) {
                    if (!isValidCustomValue(currentInputValue, dropdownOptionsData)) {
                        // clear the entry that was previously here
                        this.setDropdownInputValue(jInput, "", true);
                    }
                    this.toggleDropdownInputVisibility(jInput, true);
                } else {
                    this.setDropdownInputValue(jInput, selectedValues, true);
                    this.toggleDropdownInputVisibility(jInput, false);
                }
            } else {
                this.setDropdownInputValue(jInput, selectedValues, true);
                this.toggleDropdownInputVisibility(jInput, false);
            }
        };
        jDropdown.on("change", function () {
            updateInput();
        });
        updateInput();

        jDropdown.insertBefore(jInput);
    }

    public static resetInputFromDropdown(inputSelector: any) {
        // ensure if a dropdown associated with the input already exists, it is removed
        let jInput = jQuery(inputSelector);
        let inputDropdownId = jInput.data("dropdown");
        if (!Helper.isStringNullOrEmpty(inputDropdownId)) {
            jQuery("#" + inputDropdownId).remove();
        }

        // show the input as long as it isn't a telerik date, otherwise show the datepicker
        this.toggleDropdownInputVisibility(jInput, true);
    }

    private static toggleDropdownInputVisibility(jInput: JQuery<HTMLInputElement>, isVisible: boolean) {
        // ensure dropdown is not associated with a telerik date control, otherwise toggle that control and not the input
        let jTarget = jInput;
        let tInput = $find<Telerik.Web.UI.RadDatePicker>(jInput.attr("id") || "");
        if (tInput != null && TelerikUtility.isTelerikDateControl(tInput)) {
            jTarget = jInput.next();
        }

        if (isVisible) {
            jTarget.show();
        } else {
            jTarget.hide();
        }
    }

    private static setDropdownInputValue(jInput: JQuery<HTMLInputElement>, value: any, triggerChange: boolean) {
        // ensure dropdown is not associated with a telerik date control, otherwise set that control and not the input
        let tInput = $find<Telerik.Web.UI.RadWebControl>(jInput.attr("id") || "");
        if (tInput != null && TelerikUtility.isTelerikDateControl(tInput)) {
            // this automatically triggers an onchange event on the input control 
            // ensure the string value is properly converted to a date object
            if (value.length > 0 && value.indexOf("T") == -1) {
                // if no time is specified at all, then the date object is set to one day beofre
                value += "T00:00:00";
            }

            (<Telerik.Web.UI.RadDateTimePicker>tInput).set_selectedDate(value.length > 0 ? new Date(value) : value);
        } else if (tInput != null) {
            // default to setting the value using the general set_value method
            (<Telerik.Web.UI.RadInputControl>tInput).set_value(value);
        } else {
            jInput.val(value);
            if (triggerChange) {
                jInput.trigger("change");
            }
        }
    }

    private static getInputValueAsList(jInput: JQuery<HTMLInputElement>): string[] | undefined {
        return jInput.val()?.toString().split(",");
    }

    private static findInputValueInList(inputValue: any, dataItems: DropdownDataOption[]): DropdownDataOption | undefined {
        return dataItems.find(dataItem => dataItem.value == inputValue);
    }
}